import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import socketManager from '../lib/socket';
import { AlertTriangle, X, Info, Shield, DollarSign } from 'lucide-react';

const WARNING_TYPES = {
  trade_filtered: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  trade_blocked: { icon: X, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  config_ineligible: { icon: Shield, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  exposure_limit: { icon: DollarSign, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  daily_limit: { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  plan_block_usage_limit: { icon: DollarSign, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  plan_limit: { icon: DollarSign, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  wallet_required: { icon: Shield, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  subscription_required: { icon: Shield, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  stop_loss_failed: { icon: X, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  take_profit_failed: { icon: X, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' }
};

function formatMessage(msg) {
  // Truncate very long messages but keep key info
  if (msg.length > 120) {
    return msg.slice(0, 117) + '...';
  }
  return msg;
}

function WarningRow({ warning, onDismiss }) {
  const type = WARNING_TYPES[warning.type] || WARNING_TYPES.info;
  const Icon = type.icon;
  const timestamp = new Date(warning.timestamp).toLocaleTimeString();

  return (
    <div className={`p-2 rounded-lg border ${type.bg} ${type.border} transition-opacity hover:opacity-90`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${type.color}`} />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-dark-200">{formatMessage(warning.message)}</p>
          <p className="text-xs text-dark-500 mt-1">{timestamp}</p>
        </div>
        <button
          onClick={() => onDismiss(warning.id)}
          className="p-1 rounded hover:bg-dark-700/50 transition-colors"
          title="Dismiss"
        >
          <X className="w-3 h-3 text-dark-500" />
        </button>
      </div>
    </div>
  );
}

export default function EngineWarningsPanel({ maxVisible = 5 }) {
  const { user } = useAuthStore();
  const [warnings, setWarnings] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const dismissedRef = useRef(new Set());

  // Listen for engine warnings via socket
  useEffect(() => {
    if (!user?._id) return;

    const handleEngineWarning = (data) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const warning = { id, timestamp: new Date(), ...data };
      
      setWarnings(prev => {
        // Avoid exact duplicates within 1 second
        const recent = prev.filter(w => Date.now() - new Date(w.timestamp).getTime() < 1000);
        const isDuplicate = recent.some(w => w.type === data.type && w.message === data.message);
        if (isDuplicate) return prev;
        
        return [warning, ...prev].slice(0, 50); // Keep last 50 in memory
      });
    };

    socketManager.on('engineWarning', handleEngineWarning);
    socketManager.on('notification', handleEngineWarning); // Also catch notification events
    return () => {
      socketManager.off('engineWarning', handleEngineWarning);
      socketManager.off('notification', handleEngineWarning);
    };
  }, [user?._id]);

  const handleDismiss = (id) => {
    dismissedRef.current.add(id);
    setWarnings(prev => prev.filter(w => w.id !== id));
  };

  const visibleWarnings = warnings.filter(w => !dismissedRef.current.has(w.id)).slice(0, maxVisible);

  if (!isVisible || visibleWarnings.length === 0) return null;

  return (
    <div className="card mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400" />
          <h3 className="text-sm font-medium">Engine Warnings</h3>
          <span className="text-xs text-dark-400">({visibleWarnings.length})</span>
        </div>
        <button
          onClick={() => setIsVisible(false)}
          className="p-1 rounded hover:bg-dark-700/50 transition-colors"
          title="Hide panel"
        >
          <X className="w-3 h-3 text-dark-500" />
        </button>
      </div>
      
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {visibleWarnings.map(warning => (
          <WarningRow
            key={warning.id}
            warning={warning}
            onDismiss={handleDismiss}
          />
        ))}
      </div>
      
      {warnings.filter(w => !dismissedRef.current.has(w.id)).length > maxVisible && (
        <div className="mt-2 text-xs text-dark-500 text-center">
          +{warnings.filter(w => !dismissedRef.current.has(w.id)).length - maxVisible} more warnings
        </div>
      )}
    </div>
  );
}
